// Export the current view as a structured report (build-brief §7.5), suitable as
// an input to a NIST 800-30 risk assessment. Includes entity ids, mappings and
// PROVENANCE (source + confidence) for every assertion. Markdown and JSON.

import {
  getEntitiesByDomain,
  getMitigatingControls,
  getCounteringDefenses,
  getKevForTechnique,
  getTechniquesCoveredBy,
  getEntity,
} from './selectors';
import { normaliseAttackId } from './attack-ids';
import { DomainId } from '../data/schema';

export interface ReportParams {
  viewMode: 'explore' | 'coverage';
  domainId: string;
  domainLabel: string;
  selectedControl?: string;
  selectedTTP?: string;
}

export interface Report {
  markdown: string;
  json: string;
  filename: string;
}

const stamp = () => new Date().toISOString();

function coverageReport(domainId: string, domainLabel: string) {
  const techniques = getEntitiesByDomain(domainId as DomainId, 'offensive-technique');
  const rows = techniques.map((t) => {
    const controls = getMitigatingControls(t.id);
    const defenses = getCounteringDefenses(t.id);
    const kev = getKevForTechnique(t.id);
    return {
      id: t.id,
      name: t.name,
      mitigatingControls: controls.map((c) => ({
        id: c.control.id,
        source: c.via.source,
        confidence: c.via.confidence,
      })),
      countermeasures: defenses.map((d) => ({
        id: d.countermeasure.id,
        source: d.via.source,
        confidence: d.via.confidence,
      })),
      kevCves: kev.map((k) => ({ id: k.cve.id, source: k.via.source })),
      gap: controls.length === 0,
    };
  });
  rows.sort((a, b) => {
    const p = (r: typeof a) => (r.kevCves.length && r.gap ? 0 : r.gap ? 1 : r.kevCves.length ? 2 : 3);
    return p(a) - p(b) || a.mitigatingControls.length - b.mitigatingControls.length;
  });

  const gaps = rows.filter((r) => r.gap).length;
  const kevGaps = rows.filter((r) => r.gap && r.kevCves.length).length;

  const md: string[] = [
    `# Zero Trust Explorer — Coverage & Gap Report`,
    ``,
    `**Domain:** ${domainLabel}  `,
    `**Generated:** ${stamp()}  `,
    `**Summary:** ${rows.length} techniques · ${gaps} unmitigated · ${kevGaps} actively-exploited gaps`,
    ``,
    `| Technique | Name | Controls | D3FEND | KEV | Status |`,
    `|---|---|---|---|---|---|`,
    ...rows.map(
      (r) =>
        `| \`${r.id}\` | ${r.name} | ${r.mitigatingControls.length} | ${r.countermeasures.length} | ${r.kevCves.length ? '⚠ ' + r.kevCves.length : '–'} | ${r.gap ? '**GAP**' : 'covered'} |`,
    ),
    ``,
    `> Mappings: NIST↔ATT&CK (authoritative) · D3FEND counters (authoritative) · CISA KEV enables (snapshot). Every mapping in the JSON carries its source and confidence.`,
  ];

  return {
    markdown: md.join('\n'),
    json: { generatedAt: stamp(), view: 'coverage', domain: domainId, techniques: rows },
    filename: `ztx-coverage-${domainId.toLowerCase()}`,
  };
}

function techniqueReport(ttpId: string) {
  const anchorId = normaliseAttackId(ttpId);
  const entity = getEntity(anchorId);
  const controls = getMitigatingControls(anchorId);
  const defenses = getCounteringDefenses(anchorId);
  const kev = getKevForTechnique(anchorId);

  const md: string[] = [
    `# Zero Trust Explorer — Technique Report`,
    ``,
    `**Technique:** \`${ttpId}\` ${entity ? entity.name : ''}  `,
    `**Generated:** ${stamp()}`,
    ``,
    `## Mitigating Controls (${controls.length})`,
    ...controls.map((c) => `- \`${c.control.id}\` ${c.control.name} — _${c.via.confidence}_ (${c.via.source})`),
    ``,
    `## D3FEND Countermeasures (${defenses.length})`,
    ...defenses.map((d) => `- \`${d.countermeasure.id}\` ${d.countermeasure.name} — _${d.via.confidence}_ (${d.via.source})`),
    ``,
    `## Known Exploited Vulnerabilities (${kev.length})`,
    ...kev.map((k) => `- \`${k.cve.id}\` ${k.cve.name} — (${k.via.source})`),
  ];

  return {
    markdown: md.join('\n'),
    json: {
      generatedAt: stamp(),
      view: 'technique',
      technique: { id: ttpId, anchorId, name: entity?.name },
      mitigatingControls: controls.map((c) => ({ id: c.control.id, name: c.control.name, source: c.via.source, confidence: c.via.confidence })),
      countermeasures: defenses.map((d) => ({ id: d.countermeasure.id, name: d.countermeasure.name, source: d.via.source, confidence: d.via.confidence })),
      kevCves: kev.map((k) => ({ id: k.cve.id, name: k.cve.name, source: k.via.source })),
    },
    filename: `ztx-technique-${ttpId}`,
  };
}

function controlReport(controlId: string) {
  const entity = getEntity(controlId);
  const techniques = getTechniquesCoveredBy(controlId);
  const md: string[] = [
    `# Zero Trust Explorer — Control Report`,
    ``,
    `**Control:** \`${controlId}\` ${entity ? entity.name : ''}  `,
    `**Generated:** ${stamp()}`,
    ``,
    `## Techniques Mitigated (${techniques.length})`,
    ...techniques.map((t) => `- \`${t.technique.id}\` ${t.technique.name} — _${t.via.confidence}_ (${t.via.source})`),
  ];
  return {
    markdown: md.join('\n'),
    json: {
      generatedAt: stamp(),
      view: 'control',
      control: { id: controlId, name: entity?.name },
      techniquesMitigated: techniques.map((t) => ({ id: t.technique.id, name: t.technique.name, source: t.via.source, confidence: t.via.confidence })),
    },
    filename: `ztx-control-${controlId}`,
  };
}

export function buildReport(params: ReportParams): Report {
  let r: { markdown: string; json: unknown; filename: string };
  if (params.viewMode === 'coverage') r = coverageReport(params.domainId, params.domainLabel);
  else if (params.selectedTTP) r = techniqueReport(params.selectedTTP);
  else if (params.selectedControl) r = controlReport(params.selectedControl);
  else r = coverageReport(params.domainId, params.domainLabel);
  return { markdown: r.markdown, json: JSON.stringify(r.json, null, 2), filename: r.filename };
}

/** Trigger a client-side download of a text report. */
export function downloadReport(filename: string, content: string, type: 'md' | 'json'): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([content], {
    type: type === 'json' ? 'application/json' : 'text/markdown',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${type}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
